import React, { useState } from 'react';
import { Progress, Button } from 'antd';
import { useForm, useField } from 'react-final-form-hooks';
import SlashEye from 'assets/svg/fa-eye-slash.svg';
import Eye from 'assets/svg/eye.svg';
import { UpdatePasswordWrapper } from '../welcome/style';
import NoLoginFooter from 'components/public-components/no-login-footer';
import { observer } from 'mobx-react-lite';
import qs from 'query-string';
import useReactRouter from 'use-react-router';
import { EFirstStatus } from '@/interfaces/account-manage';
import accountManage from '@/store/account-manage';
import { useModuleTranslation } from '@/i18n/translation';

interface IPassword {
  retypedPassword: string;
  newPassword: string;
}

interface IProps {
  isLoading: boolean;
  isKeyValid?: boolean;
  changePassword: (password: string) => void;
  isResetPassword?: boolean;
}

const UpdatePassword: React.FC<IProps> = observer(props => {
  const [isPassword, setIsPassword] = useState<boolean>(true);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(true);
  const [percentage, setPercentage] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const {
    location: { search },
  } = useReactRouter();
  const t = useModuleTranslation('password');

  const username = window.atob((qs.parse(search)['username'] as string | undefined) ?? '');
  const isNew = accountManage.userInfo?.firstStatus === EFirstStatus.PASSWORD;

  const onSubmit = async (values: IPassword) => {
    if (percentage !== 100) {
      setHasError(true);
      setErrorMsg('Your password is too weak, make sure you fulfil the password strength requirement.');
      return;
    } else if (values.newPassword !== values.retypedPassword) {
      setErrorMsg(`Password entered do not match.`);
      setHasError(true);
    } else {
      setErrorMsg('');
      props.changePassword(values.newPassword);
    }
  };

  const validate = (values: IPassword) => {
    let passwordStrength = 0;
    if (values.newPassword !== undefined) {
      const lowercaseChar = /.*[a-z]/.test(values.newPassword);
      const uppercaseChar = /.*[A-Z]/.test(values.newPassword);
      const numeric = /.*[0-9]/.test(values.newPassword);
      const enoughLength = /.{8,}/.test(values.newPassword);
      if (lowercaseChar) passwordStrength += 25;
      if (uppercaseChar) passwordStrength += 25;
      if (numeric) passwordStrength += 25;
      if (enoughLength) passwordStrength += 25;
      setPercentage(passwordStrength);
    }
    return undefined;
  };

  const progressBarFormat = (percent: number | undefined) => {
    switch (percent) {
      case 25:
        return <div>Poor</div>;
      case 50:
        return <div>Medium</div>;
      case 75:
        return <div>Good</div>;
      case 100:
        return <div>Excellent</div>;
      default:
        break;
    }
  };

  const { form, handleSubmit } = useForm({
    onSubmit,
    validate,
  });

  const newPassword = useField('newPassword', form);
  const retypedPassword = useField('retypedPassword', form);

  const getStatus = () => {
    switch (percentage) {
      case 75:
        return 'normal';
      case 100:
        return 'success';
      default:
        return 'exception';
    }
  };

  return (
    <UpdatePasswordWrapper>
      <div>
        <div className="card">
          {isNew && (
            <div style={{ marginBottom: 10 }}>
              <p>{t.welcome}</p>
              <p>{t.needToChange}</p>
            </div>
          )}
          <div className="card-content">
            <div>
              <form onSubmit={handleSubmit}>
                {!props.isKeyValid && (
                  <div className="error">The link has expired. You can request for password reset again.</div>
                )}

                {!isNew && (
                  <div className="form-group">
                    <div>{username}</div>
                    <label>User Name:</label>
                  </div>
                )}

                <div className="form-group">
                  <input {...newPassword.input} id="newPassword" type={showNewPassword ? 'password' : 'text'} />
                  <label htmlFor="newPassword">New password</label>
                  <span onClick={() => setShowNewPassword(!showNewPassword)}>
                    {showNewPassword ? <SlashEye /> : <Eye />}
                  </span>
                </div>
                <div className="form-group">
                  <Progress
                    percent={percentage}
                    format={percent => <div>{progressBarFormat(percent)}</div>}
                    size="small"
                    status={getStatus()}
                  />
                  <div className="error" style={{ opacity: `${hasError ? '1' : '0'}` }}>
                    {errorMsg}
                  </div>
                </div>
                <div className="form-group">
                  <input
                    {...retypedPassword.input}
                    type={isPassword ? 'password' : 'text'}
                    className="password-input"
                    id="retypedPassword"
                  />
                  <label htmlFor="retypedPassword">Retype Password</label>
                  <span onClick={() => setIsPassword(!isPassword)}>{isPassword ? <SlashEye /> : <Eye />}</span>
                </div>
                <div>
                  <Button
                    htmlType="submit"
                    type="primary"
                    loading={props.isLoading}
                    style={{
                      fontSize: '14px',
                      marginTop: 50,
                      padding: '5px 30px',
                    }}
                    disabled={!props.isKeyValid}
                    size="large">
                    {t.change}
                  </Button>
                </div>
              </form>
            </div>
            <div className="strength-info">
              A good password is a mix with uppercase letters , lowercase letters, numbers, and at least 8 character
              length.
            </div>
          </div>
        </div>
      </div>
      <NoLoginFooter />
    </UpdatePasswordWrapper>
  );
});

UpdatePassword.defaultProps = {
  isKeyValid: true,
};

export default UpdatePassword;
